import { __awaiter } from "tslib";
import { useODataReadSingleApi, useOdataPostSingleApi, HTTPMutationVerb, useODataReadListApi, } from '../odata';
import { auth } from '../util/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOApiAuthReady } from '../odata/util/useOApiAuthReady';
import axios from 'axios';
import { ONE_HOUR_IN_MSEC } from '../../constants';
import { blobToBase64Async } from '../cryptoApi/arrayBufferConverterUtil';
import { merge } from '@fluentui/utilities';
import { endpoint, endpointIsVerified, endpointProfilePhoto, endpointClearProfilePhoto, endpointRequestVerification, endpointUserSubscriptions, } from './endpoint';
/** Odata API for accessing your own MSRC user account. */
export const useMeSingleApi = (id, options) => useODataReadSingleApi(endpoint, id, 
//Enabled by default, auth'd, and don't require id param be passed
merge({}, {
    enabled: true,
    isAuthRequired: true,
    isVerificationRequired: true,
    requireId: false,
}, options));
/** Odata API for modifying your own MSRC user account. */
export const useMePatchApi = () => useOdataPostSingleApi(endpoint, undefined, HTTPMutationVerb.PATCH);
/** Endpoint for fetching profile photo */
export const useMeProfileImageApi = () => {
    const isAuthenticatedToApi = useOApiAuthReady(true);
    return useQuery({
        queryKey: [endpointProfilePhoto],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            return yield axios
                .get(endpointProfilePhoto, {
                responseType: 'blob',
            })
                .then(({ data, headers }) => __awaiter(void 0, void 0, void 0, function* () {
                var _a, _b;
                let result = '';
                const hasImage = data.size > 0;
                if (hasImage) {
                    const base64 = yield blobToBase64Async(data);
                    const contentType = headers['content-type'] || 'image/jpeg';
                    result = `data:${contentType};base64,${base64}`;
                }
                (_b = (_a = window === null || window === void 0 ? void 0 : window.MeControl) === null || _a === void 0 ? void 0 : _a.API) === null || _b === void 0 ? void 0 : _b.setProfilePicture(result);
                return Promise.resolve(result);
            }));
        }),
        // cacheTime: 6 * ONE_HOUR_IN_MSEC,
        staleTime: 6 * ONE_HOUR_IN_MSEC,
        refetchInterval: 5 * ONE_HOUR_IN_MSEC,
        keepPreviousData: true,
        retry: false,
        enabled: isAuthenticatedToApi || false,
    });
};
/** Endpoint for modifying the user's profile photo */
export const useMeProfileImageMutationApi = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: content => {
            // Delete existing photo
            if (!content) {
                return axios.delete(endpointClearProfilePhoto);
            }
            // Add/update photo
            return axios.post(endpointProfilePhoto, content, {
                headers: {
                    'Content-Type': 'application/octet-stream',
                },
            });
        },
        onSuccess: () => {
            queryClient.refetchQueries([endpointProfilePhoto]);
        },
        mutationKey: [endpointProfilePhoto],
    });
};
/**
 * Basic useQuery rest API for checking whether a user has completed email verification.
 *
 * Returns data as a boolean.
 *
 * @example // basic usage
 * export const MyComponent = ()=>{
 * const { data, isLoading, error } = useMeEmailIsVerifiedApi();
 *
 * return (<div>
 * {error && <MessageBar>{error.message}</MessageBar>}
 * {data && <MessageBar type={MessageBarType.Success}>You were successfully verified!</MessageBar>}
 * {isLoading && <Spinner/>}
 * </div>)
 * }
 */
export const useMeEmailIsVerifiedApi = ({ enabled = true } = {}) => {
    const enabledWithAuth = useOApiAuthReady(true) && enabled;
    return useQuery({
        queryKey: [endpointIsVerified],
        enabled: enabledWithAuth,
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () { return (yield axios.get(endpointIsVerified)).data; }),
        cacheTime: Infinity,
        staleTime: Infinity,
        refetchInterval: ONE_HOUR_IN_MSEC * 4,
        retry: false,
    });
};
/** Odata API for checking whether a user has completed email verification. */
export const useMeEmailIsVerifiedODataApi = () => useODataReadSingleApi(endpointIsVerified, undefined, {
    isAuthRequired: true,
    // VERY important: we can't check *this* API from it's own call.
    isVerificationRequired: false,
    useQueryOptions: {
        cacheTime: Infinity,
        staleTime: Infinity,
        refetchInterval: ONE_HOUR_IN_MSEC * 4,
        retry: false,
    },
});
/**
 * Basic useQuery rest API for sending your one-time code for email verification.
 *
 * @example //basic usage:
 * export const MyComponent = ()=>{
 * const { useMutation, isLoading, error } = useMeRequestVerificationEmailApi();
 *
 * return (<div>
 * {error && <MessageBar>{error.message}</MessageBar>}
 * <SomeButton onClick={()=>useMutation()}/>
 * {isLoading && <Spinner/>}
 * </div>)
 * }
 */
export const useMeRequestVerificationEmailApi = () => useMutation({
    mutationKey: [endpointIsVerified],
    mutationFn: () => axios.get(endpointRequestVerification),
});
/**
 * Basic useQuery rest API for sending your one-time code for email verification.
 *
 * @example //basic usage:
 * export const MyComponent = ()=>{
 * const { useMutation, isLoading, error } = useMeOneTimeCodeApi();
 *
 * //you can use useState or you can use react-hook-form here, either work.
 * const [myOneTimeCode, setMyOneTimeCode] = useState('123abc');
 *
 * return (<div>
 * {error && <MessageBar>{error.message}</MessageBar>}
 * <SomeButton onClick={()=>useMutation(myOneTimeCode)}/>
 * {isLoading && <Spinner/>}
 * </div>)
 * }
 */
export const useMeOneTimeCodeApi = () => {
    const queryClient = useQueryClient();
    return useMutation([endpointIsVerified], {
        retry: false,
        mutationFn: (otc) => __awaiter(void 0, void 0, void 0, function* () {
            const res = yield axios
                .get(`${endpointRequestVerification}?otc=${otc}`)
                .catch(err => {
                switch (err.response.status) {
                    case 475:
                        queryClient.invalidateQueries([endpointIsVerified, endpoint]);
                        return Promise.reject(Object.assign(Object.assign({}, err), { errorType: 'OTC_INVALID', message: 'The one-time code you entered was used already, or is invalid or expired.' }));
                    case 429:
                        return Promise.reject(Object.assign(Object.assign({}, err), { errorType: 'RATE_LIMIT', message: 'You have sent too many requests at one time, please try again later.' }));
                    case 401:
                    case 403:
                        return Promise.reject(Object.assign(Object.assign({}, err), { errorType: 'AUTH' }));
                    default:
                        return Promise.reject(Object.assign(Object.assign({}, err), { errorType: 'OTHER' }));
                }
            });
            queryClient.invalidateQueries([endpointIsVerified, endpoint]);
            return res.data;
        }),
    });
};
/** Odata API for requesting a verification email for your account. */
export const useMeRequestVerificationEmailODataApi = () => useOdataPostSingleApi(endpointRequestVerification, [endpoint, endpointIsVerified], HTTPMutationVerb.POST, {
    useMutationOptions: {
        retry: false,
    },
});
/** get user notification subscriptions */
export const useMeSubscriptionListApi = (query, options) => useODataReadListApi(endpointUserSubscriptions, query, auth(merge({
    uniqueIdentifier: 'subscriptionTypeId',
}, options)));
/** Possible service options for the notifcation service */
var MeNotificationServiceEnum;
(function (MeNotificationServiceEnum) {
    MeNotificationServiceEnum[MeNotificationServiceEnum["Subscribe"] = 0] = "Subscribe";
    MeNotificationServiceEnum[MeNotificationServiceEnum["UnSubscribe"] = 1] = "UnSubscribe";
})(MeNotificationServiceEnum || (MeNotificationServiceEnum = {}));
/** Helper function for building React-query mutation APIs against `NotificationService` */
const useMeNotificationSubscribeMutationApi = (serviceName) => useOdataPostSingleApi(`${endpoint}/NotificationService.${MeNotificationServiceEnum[serviceName]}`, [
    endpoint,
    endpointUserSubscriptions,
    `${endpoint}/NotificationService.${MeNotificationServiceEnum[serviceName]}`,
]);
/** React-query API to subscribe to notification service */
export const useMeNotificationSubscribeApi = () => useMeNotificationSubscribeMutationApi(MeNotificationServiceEnum.Subscribe);
/** React-query API to unsubscribe to notification service */
export const useMeNotificationUnSubscribeApi = () => useMeNotificationSubscribeMutationApi(MeNotificationServiceEnum.UnSubscribe);
